/**
 * Dynamic deterministic color theme — Ennix Electronics  (2026 Professional Palette)
 *
 * ─── Design philosophy ────────────────────────────────────────────────────────
 *  Colors are selected from a curated 2026 trading/fintech palette:
 *  blues, teals, cyans, indigos, greens, ambers, violets — NO pink/red.
 *
 *  Resolution order (first match wins):
 *   1. Keyword scan on category slug + title  →  semantic match
 *   2. FNV-1a hash on category slug           →  fallback for unknown categories
 *
 *  Result: same category = same color (fixed), new categories auto-assigned
 *  from the palette (dynamic), always professional, never pink or red.
 *
 * ─── 2026 Professional Trading Palette ───────────────────────────────────────
 *  Inspired by Bloomberg Terminal, TradingView, Coinbase, and modern fintech.
 *  Each swatch has a primary (accent/text) and a light (subtle bg tint) variant.
 */

// ── Palette ───────────────────────────────────────────────────────────────────
const P = {
  // Blues
  ELECTRIC_BLUE : { primary: '#3b82f6', light: '#dbeafe' },   // Tailwind blue-500
  SKY_BLUE      : { primary: '#0ea5e9', light: '#e0f2fe' },   // sky-500
  ROYAL_BLUE    : { primary: '#2563eb', light: '#dbeafe' },   // blue-600
  STEEL_BLUE    : { primary: '#1d4ed8', light: '#dbeafe' },   // blue-700

  // Teals & Cyans
  TEAL          : { primary: '#1bb8a7', light: '#ccfbf1' },   // teal-500
  DARK_TEAL     : { primary: '#1bb8a7', light: '#ccfbf1' },   // teal-600
  OCEAN_TEAL    : { primary: '#0f766e', light: '#ccfbf1' },   // teal-700
  CYAN          : { primary: '#06b6d4', light: '#cffafe' },   // cyan-500
  STEEL_CYAN    : { primary: '#1bb8a7', light: '#cffafe' },   // cyan-600

  // Indigos & Violets
  INDIGO        : { primary: '#6366f1', light: '#e0e7ff' },   // indigo-500
  DEEP_INDIGO   : { primary: '#4f46e5', light: '#e0e7ff' },   // indigo-600
  VIOLET        : { primary: '#8b5cf6', light: '#ede9fe' },   // violet-500
  PURPLE        : { primary: '#7c3aed', light: '#ede9fe' },   // violet-600

  // Greens
  EMERALD       : { primary: '#10b981', light: '#d1fae5' },   // emerald-500
  GREEN         : { primary: '#22c55e', light: '#dcfce7' },   // green-500
  LIME          : { primary: '#84cc16', light: '#ecfccb' },   // lime-500
  DARK_GREEN    : { primary: '#059669', light: '#d1fae5' },   // emerald-600

  // Ambers & Golds (no red)
  GOLD          : { primary: '#f59e0b', light: '#fef3c7' },   // amber-500
  DARK_AMBER    : { primary: '#d97706', light: '#fef3c7' },   // amber-600
  AMBER_DEEP    : { primary: '#b45309', light: '#fef3c7' },   // amber-700
};

// ── Semantic keyword → color mapping ─────────────────────────────────────────
// IMPORTANT: first match wins — most specific keywords at the top.
const SEMANTIC_MAP = [
  // 🎙️ Audio / Microphones → Electric Blue  (TradingView signature)
  {
    words : ['microphone', 'mic', 'lavalier', 'lapel', 'collar mic', 'wireless mic'],
    color : P.ELECTRIC_BLUE,
    icon  : 'pi pi-volume-up',
  },

  // 🎧 Headphone / Speaker / Audio output → Purple
  {
    words : ['speaker', 'earphone', 'headphone', 'headset', 'earbuds', 'buds', 'audio'],
    color : P.PURPLE,
    icon  : 'pi pi-headphones',
  },

  // 🤳 Selfie Stick → Cyan  (mobile-first, social)
  // Must come BEFORE generic 'bluetooth/remote' so selfie stick R1 isn't hijacked
  {
    words : ['selfie stick', 'selfie-stick', 'selfie sticks', 'selfie-sticks'],
    color : P.CYAN,
    icon  : 'pi pi-mobile',
  },

  // 🔵 Bluetooth / Remote / Shutter → Sky Blue
  {
    words : ['bluetooth remote', 'shutter controller', 'bluetooth shutter', 'remote shutter'],
    color : P.SKY_BLUE,
    icon  : 'pi pi-wifi',
  },
  {
    words : ['bluetooth', 'remote control', 'wireless remote'],
    color : P.SKY_BLUE,
    icon  : 'pi pi-wifi',
  },

  // 📷 Tripods / Camera accessories → Teal  (precision, stability)
  // Comes after selfie stick so 'tripod' slug in selfie-stick slug doesn't override
  {
    words : ['tripod', 'camera stand', 'dslr stand', 'monopod'],
    color : P.TEAL,
    icon  : 'pi pi-camera',
  },

  // 📟 LCD / Tablet / Writing board → Royal Blue  (screen, digital display)
  {
    words : ['lcd', 'writing tablet', 'lcd tablet', 'e-writer', 'digital slate', 'writing board'],
    color : P.ROYAL_BLUE,
    icon  : 'pi pi-tablet',
  },

  // 🔴 Sensors / Security / Motion → Steel Cyan  (data, safety — no red)
  // MUST be before zigbee/hub because PIR sensor slugs also contain "zigbee"
  {
    words : ['pir motion', 'motion sensor', 'pir sensor', 'infrared detector',
             'infrared sensor', 'pir detector'],
    color : P.STEEL_CYAN,
    icon  : 'pi pi-shield',
  },
  {
    words : ['sensor', 'detector', 'security sensor'],
    color : P.STEEL_CYAN,
    icon  : 'pi pi-shield',
  },

  // 🔗 Zigbee / Hub / Gateway → Violet  (smart mesh, protocol)
  {
    words : ['zigbee gateway', 'matter gateway', 'matter hub', 'smart hub',
             'zigbee hub', 'gateway', 'bridge', 'thread hub'],
    color : P.VIOLET,
    icon  : 'pi pi-sitemap',
  },

  // 💡 LED / Lighting controller → Emerald  (light, energy, growth)
  // Must come BEFORE 'smart/wifi' — LED slugs often contain 'wifi'
  {
    words : ['led controller', 'led strip', 'rgb controller', 'rgbw', 'rgbcct', 'led light'],
    color : P.EMERALD,
    icon  : 'pi pi-sun',
  },
  {
    words : ['led', 'lighting', 'light controller', 'bulb', 'lamp'],
    color : P.EMERALD,
    icon  : 'pi pi-sun',
  },

  // 🏠 Smart Home / IoT / WiFi Switches → Indigo  (intelligence, automation)
  {
    words : ['smart home', 'home automation', 'smart-home', 'smart switch', 'touch switch',
             'gang switch', 'wifi switch', 'wi-fi switch', 'diy switch'],
    color : P.INDIGO,
    icon  : 'pi pi-home',
  },
  {
    words : ['smart', 'matter', 'tuya', 'iot', 'zigbee', 'wifi'],
    color : P.DEEP_INDIGO,
    icon  : 'pi pi-home',
  },

  // 🔧 Accessories / General → Dark Teal  (neutral, professional)
  {
    words : ['accessory', 'accessories'],
    color : P.DARK_TEAL,
    icon  : 'pi pi-box',
  },

  // ⚡ Power / Cable / Charger → Dark Amber  (energy, premium)
  {
    words : ['cable', 'charger', 'adapter', 'power bank', 'usb hub'],
    color : P.DARK_AMBER,
    icon  : 'pi pi-bolt',
  },
];

// ── FNV-1a hash fallback ──────────────────────────────────────────────────────
// Used only when NO keyword matches — new/uncategorised products.
const FALLBACK_PALETTE = [
  P.ELECTRIC_BLUE, P.TEAL, P.CYAN, P.INDIGO, P.EMERALD,
  P.SKY_BLUE, P.VIOLET, P.DARK_AMBER, P.ROYAL_BLUE, P.DARK_TEAL,
  P.LIME, P.STEEL_CYAN, P.GREEN, P.DEEP_INDIGO, P.GOLD, P.OCEAN_TEAL,
];

function fnvHash(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h  = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

function resolveFromKeywords(text) {
  const lower = text.toLowerCase();
  for (const entry of SEMANTIC_MAP) {
    if (entry.words.some((w) => lower.includes(w))) {
      return { color: entry.color, icon: entry.icon };
    }
  }
  return null;
}

/**
 * Get the deterministic 2026 professional color theme for a product page.
 *
 * @param {string} categorySlug   category slug from API  (e.g. "microphones")
 * @param {string} categoryTitle  category title from API (e.g. "Microphones")
 * @param {string} fallbackSlug   product slug/id — used when no category set
 * @returns {{ primary: string, light: string, icon: string }}
 */
export function getCategoryTheme(categorySlug = '', categoryTitle = '', fallbackSlug = '') {
  // Combine all available text for keyword scanning
  const text = [categoryTitle, categorySlug, fallbackSlug].filter(Boolean).join(' ');

  // 1. Semantic keyword match — always returns a professional 2026 color
  const match = resolveFromKeywords(text);
  if (match) {
    return { ...match.color, icon: match.icon };
  }

  // 2. Hash fallback — for genuinely uncategorised products
  const key   = (categorySlug || fallbackSlug || 'default').toLowerCase().trim();
  const idx   = fnvHash(key) % FALLBACK_PALETTE.length;
  const color = FALLBACK_PALETTE[idx];

  return { ...color, icon: 'pi pi-box' };
}
